import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { GlobalNav } from "../shared/GlobalNav";
import { CreateSubscriptionModal } from "./CreateSubscriptionModal";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import {
  RefreshCw, Plus, CheckCircle, Clock, Banknote, Copy, Send,
  MoreHorizontal, Archive, Trash2, RotateCcw, XCircle, Users, Mail, Lock, Crown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientSubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [sendingInvitation, setSendingInvitation] = useState(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { token, user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const hasPlusSubscription = user?.subscription_plan === 'paidd_plus' || user?.subscription_plan === 'embedded';

  useEffect(() => {
    fetchSubscriptions();
  }, [filter]);

  const fetchSubscriptions = async () => {
    try {
      const includeArchived = filter === 'archived';
      const response = await axios.get(`${API}/subscriptions?include_archived=${includeArchived}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch subscriptions", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async (subscriptionId) => {
    setSendingInvitation(subscriptionId);
    try {
      await axios.post(`${API}/subscriptions/${subscriptionId}/send-invitation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Invitation Sent", description: "Your client will receive an email with the subscription link." });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to send invitation", variant: "destructive" });
    } finally {
      setSendingInvitation(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancellingId) return;
    try {
      await axios.post(`${API}/subscriptions/${cancellingId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Subscription Cancelled", description: "The subscription has been cancelled successfully." });
      fetchSubscriptions();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to cancel subscription", variant: "destructive" });
    } finally {
      setShowCancelConfirm(false);
      setCancellingId(null);
    }
  };

  const handleRenewSubscription = async (subscriptionId) => {
    try {
      await axios.post(`${API}/subscriptions/${subscriptionId}/renew`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Subscription Renewed", description: "A new invitation link has been generated. Send it to your client." });
      fetchSubscriptions();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to renew subscription", variant: "destructive" });
    }
  };

  const handleArchiveSubscription = async () => {
    if (!archivingId) return;
    try {
      await axios.post(`${API}/subscriptions/${archivingId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Subscription Archived", description: "The subscription has been archived successfully." });
      fetchSubscriptions();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to archive subscription", variant: "destructive" });
    } finally {
      setShowArchiveConfirm(false);
      setArchivingId(null);
    }
  };

  const handleUnarchiveSubscription = async (subscriptionId) => {
    try {
      await axios.post(`${API}/subscriptions/${subscriptionId}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Subscription Restored", description: "The subscription has been restored successfully." });
      fetchSubscriptions();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to restore subscription", variant: "destructive" });
    }
  };

  const handleDeleteSubscription = async () => {
    if (!deletingId) return;
    try {
      await axios.delete(`${API}/subscriptions/${deletingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Subscription Deleted", description: "The subscription has been permanently deleted." });
      fetchSubscriptions();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete subscription", variant: "destructive" });
    } finally {
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };

  const copySubscriptionLink = (subscription) => {
    const link = `${window.location.origin}/subscription/${subscription.slug}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Link Copied", description: "Subscription link copied to clipboard." });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      paused: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Paused' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
      expired: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Expired' },
      archived: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Archived' },
      payment_failed: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Payment Failed' }
    };
    const style = statusStyles[status] || statusStyles.pending;
    return <Badge className={`${style.bg} ${style.text} font-body`}>{style.label}</Badge>;
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter === 'active') return sub.status === 'active';
    if (filter === 'pending') return sub.status === 'pending';
    if (filter === 'cancelled') return sub.status === 'cancelled';
    if (filter === 'archived') return sub.status === 'archived';
    return true;
  });

  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter(s => s.status === 'active').length,
    pending: subscriptions.filter(s => s.status === 'pending').length,
    monthlyRecurring: subscriptions
      .filter(s => s.status === 'active')
      .reduce((sum, s) => sum + (s.amount || 0), 0)
  };

  if (!hasPlusSubscription) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="subscriptions" />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-[#e6007e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#e6007e]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Upgrade Required</h2>
              <p className="text-gray-600 mb-6 font-body">
                Client Subscriptions are available with <strong>Paify Plus</strong> or <strong>Embedded</strong> plans.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-2 font-heading">What you get:</h3>
                <ul className="space-y-2 text-sm text-gray-600 font-body">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Unlimited client subscriptions</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Automatic monthly billing</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Client self-service portal</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Contract signing included</li>
                </ul>
              </div>
              <Button onClick={() => navigate('/subscriptions')} className="bg-[#e6007e] hover:bg-[#c90068] text-white font-body">
                <Crown className="w-4 h-4 mr-2" />Upgrade to Paify Plus
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="subscriptions" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading subscriptions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="retainers" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 font-heading">Client Subscriptions</h1>
              <p className="text-sm md:text-base text-gray-600 font-body">Manage recurring monthly billing for your clients.</p>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="bg-[#e6007e] hover:bg-[#c90068] text-white font-body" data-testid="create-subscription-btn">
              <Plus className="w-4 h-4 mr-2" />Create Subscription
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6">
          {[
            { icon: RefreshCw, color: '[#e6007e]', value: stats.total, label: 'Total' },
            { icon: CheckCircle, color: 'green-600', bgColor: 'green-100', value: stats.active, label: 'Active' },
            { icon: Clock, color: 'yellow-600', bgColor: 'yellow-100', value: stats.pending, label: 'Pending' },
            { icon: Banknote, color: 'blue-600', bgColor: 'blue-100', value: `\u00A3${stats.monthlyRecurring.toLocaleString()}`, label: 'Monthly' },
          ].map((stat, i) => (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center">
                  <div className={`p-2 ${stat.bgColor ? `bg-${stat.bgColor}` : 'bg-[#e6007e]/10'} rounded-lg`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-2xl font-bold text-gray-900 font-heading">{stat.value}</p>
                    <p className="text-sm text-gray-600 font-body">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'active', 'pending', 'cancelled', 'archived'].map((f) => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} className={filter === f ? 'bg-black text-white' : ''}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        {/* Subscriptions List */}
        {filteredSubscriptions.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">
                {filter === 'all' ? 'No subscriptions yet' : `No ${filter} subscriptions`}
              </h3>
              <p className="text-gray-600 mb-6 font-body">Create your first subscription to start billing clients monthly.</p>
              {filter === 'all' && (
                <Button onClick={() => setShowCreateModal(true)} className="bg-[#e6007e] hover:bg-[#c90068] text-white font-body">
                  <Plus className="w-4 h-4 mr-2" />Create Subscription
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredSubscriptions.map((subscription) => (
              <Card key={subscription.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate font-heading">{subscription.title}</h3>
                        {getStatusBadge(subscription.status)}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 font-body">
                        <span className="flex items-center gap-1"><Users className="w-4 h-4" />{subscription.client_name}</span>
                        <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{subscription.client_email}</span>
                        <span className="flex items-center gap-1 font-semibold text-[#e6007e]">&pound;{subscription.amount?.toFixed(2)}/month</span>
                      </div>
                      {subscription.status === 'active' && subscription.next_billing_date && (
                        <p className="text-xs text-gray-500 mt-2 font-body">Next billing: {new Date(subscription.next_billing_date).toLocaleDateString()}</p>
                      )}
                    </div>
                    <SubscriptionActions
                      subscription={subscription}
                      sendingInvitation={sendingInvitation}
                      onCopyLink={() => copySubscriptionLink(subscription)}
                      onSendInvitation={() => handleSendInvitation(subscription.id)}
                      onCancel={() => { setCancellingId(subscription.id); setShowCancelConfirm(true); }}
                      onRenew={() => handleRenewSubscription(subscription.id)}
                      onArchive={() => { setArchivingId(subscription.id); setShowArchiveConfirm(true); }}
                      onUnarchive={() => handleUnarchiveSubscription(subscription.id)}
                      onDelete={() => { setDeletingId(subscription.id); setShowDeleteConfirm(true); }}
                    />
                  </div>
                  <SubscriptionInvoiceList subscriptionId={subscription.id} token={token} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <CreateSubscriptionModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={() => fetchSubscriptions()} />

      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">This will stop all future billing. The client will be notified. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">Keep Subscription</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelSubscription} className="bg-red-600 hover:bg-red-700 text-white font-body">Yes, Cancel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Archive Subscription?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">This subscription will be hidden from your main list. You can restore it anytime from the Archived filter.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveSubscription} className="bg-yellow-600 hover:bg-yellow-700 text-white font-body">Yes, Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Subscription?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">This will permanently delete this subscription. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSubscription} className="bg-red-600 hover:bg-red-700 text-white font-body">Yes, Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const SubscriptionActions = ({ subscription, sendingInvitation, onCopyLink, onSendInvitation, onCancel, onRenew, onArchive, onUnarchive, onDelete }) => {
  const MoreMenu = ({ children }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{children}</DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex items-center gap-2">
      {subscription.status === 'pending' && (
        <>
          <Button variant="outline" size="sm" onClick={onCopyLink} className="font-body"><Copy className="w-4 h-4 mr-1" />Copy Link</Button>
          <Button size="sm" onClick={onSendInvitation} disabled={sendingInvitation === subscription.id} className="bg-[#e6007e] hover:bg-[#c90068] text-white font-body">
            <Send className="w-4 h-4 mr-1" />{sendingInvitation === subscription.id ? 'Sending...' : 'Send Invite'}
          </Button>
          <MoreMenu>
            <DropdownMenuItem onClick={onArchive} className="text-yellow-600"><Archive className="w-4 h-4 mr-2" />Archive</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
          </MoreMenu>
        </>
      )}
      {subscription.status === 'active' && (
        <Button variant="outline" size="sm" onClick={onCancel} className="text-red-600 hover:text-red-700 hover:bg-red-50 font-body">
          <XCircle className="w-4 h-4 mr-1" />Cancel
        </Button>
      )}
      {subscription.status === 'payment_failed' && (
        <>
          <Button variant="outline" size="sm" onClick={onCopyLink} className="font-body"><Copy className="w-4 h-4 mr-1" />Copy Link</Button>
          <Button size="sm" onClick={onSendInvitation} disabled={sendingInvitation === subscription.id} className="bg-orange-500 hover:bg-orange-600 text-white font-body">
            <Send className="w-4 h-4 mr-1" />Resend to Retry
          </Button>
          <MoreMenu>
            <DropdownMenuItem onClick={onArchive} className="text-yellow-600"><Archive className="w-4 h-4 mr-2" />Archive</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
          </MoreMenu>
        </>
      )}
      {subscription.status === 'cancelled' && (
        <>
          <Button variant="outline" size="sm" onClick={onRenew} className="font-body"><RotateCcw className="w-4 h-4 mr-1" />Renew</Button>
          <MoreMenu>
            <DropdownMenuItem onClick={onArchive} className="text-yellow-600"><Archive className="w-4 h-4 mr-2" />Archive</DropdownMenuItem>
            {(subscription.payment_count || 0) === 0 && (
              <DropdownMenuItem onClick={onDelete} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
            )}
          </MoreMenu>
        </>
      )}
      {subscription.status === 'archived' && (
        <>
          <Button variant="outline" size="sm" onClick={onUnarchive} className="font-body"><RotateCcw className="w-4 h-4 mr-1" />Restore</Button>
          {(subscription.payment_count || 0) === 0 && (
            <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50 font-body">
              <Trash2 className="w-4 h-4 mr-1" />Delete
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default ClientSubscriptionsPage;

// ---------------------------------------------------------------------------
// Subscription invoice history (per-subscription)
// ---------------------------------------------------------------------------
function SubscriptionInvoiceList({ subscriptionId, token }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const { toast } = useToast();

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/subscriptions/${subscriptionId}/invoices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoices(res.data?.invoices || []);
    } catch (err) {
      toast({
        title: "Could not load invoices",
        description: err.response?.data?.detail || "Failed to fetch invoices",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && invoices.length === 0) fetchInvoices();
  };

  const handleResend = async (invoiceNumber) => {
    setResendingId(invoiceNumber);
    try {
      const res = await axios.post(
        `${API}/subscriptions/${subscriptionId}/invoices/${invoiceNumber}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Invoice re-sent", description: res.data?.message });
    } catch (err) {
      toast({
        title: "Could not resend",
        description: err.response?.data?.detail || "Failed to resend invoice",
        variant: "destructive",
      });
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <button
        type="button"
        data-testid={`toggle-invoices-${subscriptionId}`}
        onClick={handleToggle}
        className="text-sm font-body text-gray-600 hover:text-[#e6007e] inline-flex items-center gap-1"
      >
        {open ? "Hide" : "Show"} invoice history{invoices.length ? ` (${invoices.length})` : ""}
      </button>

      {open && (
        <div className="mt-3" data-testid={`invoices-${subscriptionId}`}>
          {loading ? (
            <p className="text-sm text-gray-500 font-body">Loading…</p>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-gray-500 font-body">
              No invoices yet. Invoices will appear here once Stripe processes the first payment.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead className="text-left text-gray-500 uppercase text-xs">
                  <tr>
                    <th className="py-2 pr-3">Invoice #</th>
                    <th className="py-2 pr-3">Period</th>
                    <th className="py-2 pr-3">Paid on</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.invoice_number} className="border-t border-gray-100">
                      <td className="py-2 pr-3 font-medium">{inv.invoice_number}</td>
                      <td className="py-2 pr-3 text-gray-600">
                        {inv.period_start || "—"}{inv.period_end ? ` – ${inv.period_end}` : ""}
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{inv.paid_at || "—"}</td>
                      <td className="py-2 pr-3 text-right font-semibold">
                        £{(inv.amount ?? 0).toFixed(2)}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resendingId === inv.invoice_number}
                          onClick={() => handleResend(inv.invoice_number)}
                          data-testid={`resend-invoice-${inv.invoice_number}`}
                          className="font-body"
                        >
                          {resendingId === inv.invoice_number ? "Sending…" : "Re-email"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
